import React, { useState, useEffect, useRef } from "react";
import useRouting from "../hooks/useRouting";
import usePageTracking from "../hooks/usePageTracking";
import { trackEvent } from "../utils/trackEvent";
import environment from "../environment";
import axios from "axios";
import { getHeaders, getStorage } from "../utils/apiUtils";
import Header from "./Header";
import Autocomplete from "./Autocomplete";
import GenericAutocomplete from "./GenericAutocomplete";
import "./PatientInfo.css";
import iconTreatmeentDetails from "../assets/images/iconTreatmeentDetails.svg";

const PatientInfo = () => {
  const hasFetchedRef = useRef(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [relationship, setRelationship] = useState("");
  //const inputRef = useRef(null);


  const handleCitySelect = (selectedCity) => {
    // Clear hospital when city changes since hospitals are city-specific
    setCity(selectedCity);
    if (hospital) {
      setHospital("");
      setHospitalName("");
      setCustomHospital("");
    }
  };
  const [hospital, setHospital] = useState(""); // Store hospital ID
  const [hospitalName, setHospitalName] = useState(""); // Store hospital name for display
  const [city, setCity] = useState("");
  const [customHospital, setCustomHospital] = useState("");
  const [isPatient, setIsPatient] = useState(true);
  const { navigateWithContext } = useRouting();

  // Track page visit
  usePageTracking("patient-info");
  useEffect(() => { trackEvent("loan_patient_info_viewed"); }, []);

  // Fetch personal data to prefill patient information
  const fetchPersonalData = async () => {
    try {
      const applicationId = getStorage("application_id");
      if (!applicationId) {
        console.error("Application ID not found in localStorage");
        return;
      }

      const endpoint = `${environment.apiurl}applications/${applicationId}/view/data/?allowed=personal`;
      const response = await axios.get(endpoint, { headers: getHeaders() });

      if (response?.data?.msg?.personal) {
        const personalData = response.data.msg.personal;

        // Prefill patient details if available
        if (personalData.patient_first_name) {
          setFirstName(personalData.patient_first_name);
        }
        if (personalData.patient_last_name) {
          setLastName(personalData.patient_last_name);
        }
        if (personalData.relation_with_borrower) {
          setRelationship(personalData.relation_with_borrower);
          setIsPatient("no"); // If relationship exists, patient is not the borrower
        } else if (
          personalData.patient_first_name ||
          personalData.patient_last_name
        ) {
          setIsPatient("yes"); // If patient name exists but no relationship, borrower is the patient
        }
      }

      // Also fetch application data for hospital and city
      const appEndpoint = `${environment.apiurl}applications/${applicationId}/view/data/?allowed=application,application_hospital`;
      const appResponse = await axios.get(appEndpoint, {
        headers: getHeaders(),
      });

      if (appResponse?.data?.msg) {
        const applicationData = appResponse.data.msg.application;
        const applicationHospital = appResponse.data.msg.application_hospital;
        
        // Prefill city and hospital from application_hospital
        if (applicationHospital) {
          // Prefill city from application_hospital
          if (applicationHospital.city) {
            setCity(applicationHospital.city);
          }
          
          // Prefill hospital ID and name from application_hospital
          // application_hospital has id, name, and city directly
          if (applicationHospital.id) {
            setHospital(applicationHospital.id);
          }
          
          if (applicationHospital.name) {
            setHospitalName(applicationHospital.name);
          }
        } else {
          // Fallback to application data if application_hospital not available
          if (applicationData?.hospital_city) {
            setCity(applicationData.hospital_city);
          }
          
          if (applicationData?.hospital) {
            // If hospital is an object with id and name
            if (typeof applicationData.hospital === 'object') {
              setHospital(applicationData.hospital.id || applicationData.hospital);
              setHospitalName(applicationData.hospital.name || "");
            } else {
              // If hospital is just an ID, fetch the name
              setHospital(applicationData.hospital);
              try {
                const hospitalResponse = await axios.get(
                  `${environment.apiurl_agentCode}hospitals/list/`,
                  {
                    headers: getHeaders(),
                    params: {
                      filters: JSON.stringify([
                        { key: "id", value: applicationData.hospital }
                      ]),
                    },
                  }
                );
                const hospitals = hospitalResponse?.data?.msg?.items || [];
                if (hospitals.length > 0 && hospitals[0].name) {
                  setHospitalName(hospitals[0].name);
                }
              } catch (error) {
                console.error("Error fetching hospital name:", error);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error("Error fetching personal data:", error);
      // Don't show error to user for prefill failures
    }
  };

  // Fetch subscriber details when user selects "yes"
  const fetchSubscriberDetails = async () => {
    try {
      const applicationId = getStorage("application_id");
      if (!applicationId) {
        console.error("Application ID not found in localStorage");
        return;
      }

      const endpoint = `${environment.apiurlv3_webapi}applications/${applicationId}/view/data/?allowed=personal`;
      const response = await axios.get(endpoint, { headers: getHeaders() });

      if (response?.data?.msg) {
        const data = response.data.msg;
        console.log(data, "data from personal_subscription api")
        
        // Check for personal_subscription data
        if (data.personal_subscription) {
          const subscriptionData = data.personal_subscription;
          
          // Auto-fill first name and last name from subscriber details
          if (subscriptionData.first_name) {
            setFirstName(subscriptionData.first_name);
          }
          if (subscriptionData.last_name) {
            setLastName(subscriptionData.last_name);
          }
        }
        // Fallback to personal data if subscription data not available
        else if (data.personal) {
          const personalData = data.personal;
          if (personalData.first_name) {
            setFirstName(personalData.first_name);
          }
          if (personalData.last_name) {
            setLastName(personalData.last_name);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching subscriber details:", error);
      // Don't show error to user, just log it
    }
  };

  useEffect(() => {
    // Prevent duplicate API calls in StrictMode
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchPersonalData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array ensures this runs only once on mount

  const handlePatientChange = async (value) => {
    setIsPatient(value);
    // Reset patient details when changing selection
    if (value === "yes") {
      setFirstName("");
      setLastName("");
      setRelationship("");
      // Fetch and auto-fill subscriber details
      await fetchSubscriberDetails();
    } else {
      // Reset fields when selecting "no"
      setFirstName("");
      setLastName("");
      setRelationship("");
    }
  };

  const handleHospitalSelect = (selectedHospital) => {
    if (selectedHospital.name && selectedHospital.name.toLowerCase() === 'other') {
      setHospital('other');
      setHospitalName('other');
    } else {
      // Store ID for API and name for display
      setHospital(selectedHospital.id);
      setHospitalName(selectedHospital.name);
      setCustomHospital('');
    }
  };

  const handleContinue = async () => {
    // Validate form including custom hospital when "other" is selected
    const isValidHospital =
      hospital === "other" ? customHospital.trim() : hospital;

    trackEvent("loan_patient_info_continue_clicked", {
      is_patient: isPatient,
      relationship: isPatient ? null : relationship,
      city,
    });

    const patientData = {
      isPatient,
      firstName,
      lastName,
      relationship: isPatient === "no" ? relationship : "",
      city,
      hospital: isValidHospital,
    };

    const isValid =
      isPatient &&
      city &&
      isValidHospital &&
      firstName &&
      lastName &&
      (isPatient === "yes" || relationship);

    if (isValid) {
      try {
        // Prepare API payload
        const payload = {
          data: [
            {
              model_name: "application",
              field: [
                {
                  key: "hospital",
                  value: hospital === "other" ? customHospital : hospital,
                }
              ],
            },
            {
              model_name: "personal",
              field: [
                {
                  key: "patient_first_name",
                  value: firstName,
                },
                {
                  key: "patient_last_name",
                  value: lastName,
                },
                {
                  key: "is_patient",
                  value: isPatient,
                },
                ...(isPatient === "no"
                  ? [
                      {
                        key: "relation_with_borrower",
                        value: relationship,
                      },
                    ]
                  : []),
              ],
            },
          ],
        };

        // Call API to save patient details
        const applicationId = getStorage("application_id");
        await axios.post(
          `${environment.apiurl}applications/${applicationId}/save/data/`,
          payload,
          { headers: getHeaders() }
        );

        // Save to localStorage after successful API call
        localStorage.setItem("patientInfo", JSON.stringify(patientData));
        navigateWithContext("personal-details");
      } catch (error) {
        console.error("Error saving patient details:", error);
        // Still navigate on error for better UX
        localStorage.setItem("patientInfo", JSON.stringify(patientData));
        navigateWithContext("personal-details");
      }
    }
  };

  const onBackClick = () => {
    navigateWithContext("loan-type");
  };
  const relationships = [
    "Spouse",
    "Father",
    "Mother",
    "Son",
    "Daughter",
    "Brother",
    "Sister",
    "Father-in-law",
    "Mother-in-law"
  ];

  const isFormValid =
    isPatient &&
    city &&
    hospital &&
    (hospital !== "others" || customHospital.trim()) &&
    firstName &&
    lastName &&
    (isPatient === "yes" || relationship);

  return (
    <div className="loan-container">
      <Header showBackButton={true} onBackClick={onBackClick} />

      <div className="main-content">
        <div className="hero-section">
          <div className="hero-icon">
            <img src={iconTreatmeentDetails} alt="Treatmeent Details" />
          </div>
          <h1 className="hero-title">
            Tell us about
            <br />
            the patient
          </h1>
          <p className="hero-subtitle">
            Who needs care and where
            <br />
            they&apos;re being treated
          </p>
        </div>

        <div className="form-section">
          <div className="patient-question-card">
            <h2 className="form-title">Are you the patient?</h2>
            <p
              style={{
                fontSize: "14px",
                color: "#666",
                marginBottom: "20px",
                textAlign: "left",
              }}
            >
              Choose the right option
            </p>

            <div className="radio-group">
              <div
                className={`radio-option ${
                  isPatient === "yes" ? "selected" : ""
                }`}
                onClick={() => handlePatientChange("yes")}
              >
                <div className="card-content">
                  <div className="card-title">Yes</div>
                </div>
                <input
                  type="radio"
                  className="radio-input"
                  name="isPatient"
                  value="yes"
                  checked={isPatient === "yes"}
                  onChange={() => handlePatientChange("yes")}
                />
                <label className="radio-label"></label>
              </div>

              <div
                className={`radio-option ${
                  isPatient === "no" ? "selected" : ""
                }`}
                onClick={() => handlePatientChange("no")}
              >
                <div className="card-content">
                  <div className="card-title">No</div>
                </div>
                <input
                  type="radio"
                  className="radio-input"
                  name="isPatient"
                  value="no"
                  checked={isPatient === "no"}
                  onChange={() => handlePatientChange("no")}
                />
                <label className="radio-label"></label>
              </div>
            </div>
          </div>

          {isPatient === "yes" && (
            <div className="patient-details-card">
              <h3 className="section-title">Patient Details</h3>

              <div className="form-group-container">
                <label className="form-label  -ml-[4px]">First Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Ravi"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>

              <div className="form-group-container">
                <label className="form-label -ml-[4px]">Last Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Kumar"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
            </div>
          )}

          {isPatient === "no" && (
            <div className="patient-details-card">
              <h3 className="section-title">Patient Details</h3>

              <div className="form-group-container">
                <label className="form-label -ml-[4px]">First Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Arjun"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>

              <div className="form-group-container">
                <label className="form-label -ml-[4px]">Last Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Singh"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>

              <div className="form-group-container">
                <label className="form-label -ml-[4px]">Relation with Subscriber <span className="text-red-500">*</span></label>
                <select
                  className="form-select"
                  value={relationship}
                  onChange={(e) => setRelationship(e.target.value)}
                >
                  <option value="">Choose the right option</option>
                  {relationships.map((rel, index) => (
                    <option key={index} value={rel.toLowerCase()}>
                      {rel}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {(isPatient === "yes" || isPatient === "no") && (
            <div className="patient-details-card">
              <h3 className="section-title">Hospital Details</h3>

              <div className="form-group-container">
                <label className="form-label -ml-[4px]">City <span className="text-red-500">*</span></label>
                <Autocomplete 
                  onSelect={handleCitySelect} 
                  value={city}
                />
              </div>

              <div className="form-group-container">
                <label className="form-label -ml-[4px]">Your Current Hospital <span className="text-red-500">*</span></label>
                {/* <GenericAutocomplete             
                  onSelect={handleHospitalSelect}
                  apiUrl={`${environment.apiurl_agentCode}hospitals/list/`}
                  buildFilters={(inputValue, city) => [
                    { key: "name", value: inputValue },
                    { key: "city", value: city }
                  ]}
                  displayField="name"
                  placeholder="Search your hospital"
                  value={hospitalName}
                  dependencies={[city]}
                /> */}
                <GenericAutocomplete             
  onSelect={handleHospitalSelect}
  apiUrl={`${environment.apiurl_agentCode}hospitals/list/`}
  buildFilters={(inputValue, selectedCity) => [
    { key: "name", value: inputValue },
    { key: "city", value: selectedCity }
  ]}
  selectedCity={city}
  displayField="name"
  placeholder="Search your hospital"
  value={hospitalName}
  dependencies={[city]}
/>

              </div>
            </div>
          )}
        </div>
      </div>

      <div className="fixed-button-container">
        <button
          className="continue-button"
          onClick={handleContinue}
          disabled={!isFormValid}
        >
          CONTINUE
        </button>
      </div>
    </div>
  );
};

export default PatientInfo;
